import React, { useState } from 'react';
import { Plus, Shield, Edit2, Trash2, X, Users, Lock, ChevronDown, ChevronRight, Copy } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { Permission, permissionCategories, allPermissions } from '../data/workspaceData';
export function RolesPermissions() {
  const {
    roles,
    allUsers,
    currentUser,
    createRole,
    updateRole,
    deleteRole
  } = useWorkspace();
  const [selectedRole, setSelectedRole] = useState<number | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [editingRole, setEditingRole] = useState<number | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(Object.keys(permissionCategories)));
  const [newRole, setNewRole] = useState({
    name: '',
    description: '',
    permissions: [] as Permission[]
  });
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    permissions: [] as Permission[]
  });
  // Security check
  if (currentUser.role !== 'Super Admin') {
    return <div className="p-8 text-center" data-id="element-1514">
        <Shield className="mx-auto h-12 w-12 text-gray-400" data-id="element-1515" />
        <h2 className="mt-4 text-lg font-medium text-gray-900" data-id="element-1516">
          Access Denied
        </h2>
        <p className="mt-2 text-sm text-gray-500" data-id="element-1517">
          Only Super Admins can manage roles and permissions.
        </p>
      </div>;
  }
  const toggleCategory = (category: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };
  const togglePermission = (permission: Permission, isEdit: boolean) => {
    if (isEdit) {
      setEditForm(prev => ({
        ...prev,
        permissions: prev.permissions.includes(permission) ? prev.permissions.filter(p => p !== permission) : [...prev.permissions, permission]
      }));
    } else {
      setNewRole(prev => ({
        ...prev,
        permissions: prev.permissions.includes(permission) ? prev.permissions.filter(p => p !== permission) : [...prev.permissions, permission]
      }));
    }
  };
  const toggleAllInCategory = (category: string, permissions: string[], isEdit: boolean) => {
    const perms = permissions as Permission[];
    if (isEdit) {
      const allSelected = perms.every(p => editForm.permissions.includes(p));
      if (allSelected) {
        setEditForm(prev => ({
          ...prev,
          permissions: prev.permissions.filter(p => !perms.includes(p))
        }));
      } else {
        setEditForm(prev => ({
          ...prev,
          permissions: [...new Set([...prev.permissions, ...perms])]
        }));
      }
    } else {
      const allSelected = perms.every(p => newRole.permissions.includes(p));
      if (allSelected) {
        setNewRole(prev => ({
          ...prev,
          permissions: prev.permissions.filter(p => !perms.includes(p))
        }));
      } else {
        setNewRole(prev => ({
          ...prev,
          permissions: [...new Set([...prev.permissions, ...perms])]
        }));
      }
    }
  };
  const handleCreateRole = () => {
    if (newRole.name.trim()) {
      createRole(newRole);
      setNewRole({
        name: '',
        description: '',
        permissions: []
      });
      setIsCreating(false);
    }
  };
  const handleStartEdit = (roleId: number) => {
    const role = roles.find(r => r.id === roleId);
    if (role) {
      setEditForm({
        name: role.name,
        description: role.description,
        permissions: [...role.permissions]
      });
      setEditingRole(roleId);
    }
  };
  const handleSaveEdit = () => {
    if (editingRole && editForm.name.trim()) {
      updateRole(editingRole, editForm);
      setEditingRole(null);
    }
  };
  const handleDuplicateRole = (roleId: number) => {
    const role = roles.find(r => r.id === roleId);
    if (role) {
      createRole({
        name: `${role.name} (Copy)`,
        description: role.description,
        permissions: [...role.permissions]
      });
    }
  };
  const getUsersWithRole = (roleId: number) => {
    return allUsers.filter(u => u.roleId === roleId);
  };
  const formatPermissionName = (permission: string) => {
    const [, action] = permission.split('.');
    return action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };
  const renderPermissionEditor = (permissions: Permission[], isEdit: boolean) => {
    const currentPermissions = isEdit ? editForm.permissions : newRole.permissions;
    return <div className="space-y-2" data-id="element-1518">
        {Object.entries(permissionCategories).map(([category, perms]) => {
        const isExpanded = expandedCategories.has(category);
        const selectedCount = perms.filter(p => currentPermissions.includes(p as Permission)).length;
        const allSelected = selectedCount === perms.length;
        return <div key={category} className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-1519">
              <button type="button" onClick={() => toggleCategory(category)} className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors" data-id="element-1520">
                <div className="flex items-center gap-3" data-id="element-1521">
                  {isExpanded ? <ChevronDown className="h-4 w-4 text-gray-500" data-id="element-1522" /> : <ChevronRight className="h-4 w-4 text-gray-500" data-id="element-1523" />}
                  <span className="font-medium text-gray-900" data-id="element-1524">{category}</span>
                  <span className="text-xs text-gray-500" data-id="element-1525">
                    ({selectedCount}/{perms.length})
                  </span>
                </div>
                <button type="button" onClick={e => {
              e.stopPropagation();
              toggleAllInCategory(category, perms, isEdit);
            }} className={`text-xs px-2 py-1 rounded ${allSelected ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-600'} hover:opacity-80`} data-id="element-1526">
                  {allSelected ? 'Deselect All' : 'Select All'}
                </button>
              </button>
              {isExpanded && <div className="p-4 grid grid-cols-2 gap-2" data-id="element-1527">
                  {perms.map(perm => <label key={perm} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded" data-id="element-1528">
                      <input type="checkbox" checked={currentPermissions.includes(perm as Permission)} onChange={() => togglePermission(perm as Permission, isEdit)} className="h-4 w-4 text-blue-600 rounded border-gray-300" data-id="element-1529" />
                      <span className="text-sm text-gray-700" data-id="element-1530">
                        {formatPermissionName(perm)}
                      </span>
                    </label>)}
                </div>}
            </div>;
      })}
      </div>;
  };
  return <div className="space-y-6" data-id="element-1531">
      <div className="flex items-center justify-between" data-id="element-1532">
        <div data-id="element-1533">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-1534">
            Roles & Permissions
          </h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-1535">
            Manage user roles and their access permissions.
          </p>
        </div>
        <button onClick={() => setIsCreating(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-1536">
          <Plus className="h-4 w-4" data-id="element-1537" />
          Create Role
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4" data-id="element-1538">
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-1539">
          <div className="flex items-center gap-3" data-id="element-1540">
            <div className="p-2 bg-purple-50 rounded-lg" data-id="element-1541">
              <Shield className="h-5 w-5 text-purple-600" data-id="element-1542" />
            </div>
            <div data-id="element-1543">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-1544">
                {roles.length}
              </p>
              <p className="text-xs text-gray-500" data-id="element-1545">Total Roles</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-1546">
          <div className="flex items-center gap-3" data-id="element-1547">
            <div className="p-2 bg-blue-50 rounded-lg" data-id="element-1548">
              <Lock className="h-5 w-5 text-blue-600" data-id="element-1549" />
            </div>
            <div data-id="element-1550">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-1551">
                {roles.filter(r => r.isSystem).length}
              </p>
              <p className="text-xs text-gray-500" data-id="element-1552">System Roles</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-1553">
          <div className="flex items-center gap-3" data-id="element-1554">
            <div className="p-2 bg-green-50 rounded-lg" data-id="element-1555">
              <Users className="h-5 w-5 text-green-600" data-id="element-1556" />
            </div>
            <div data-id="element-1557">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-1558">
                {allPermissions.length}
              </p>
              <p className="text-xs text-gray-500" data-id="element-1559">Available Permissions</p>
            </div>
          </div>
        </div>
      </div>

      {/* Create Role Modal */}
      {isCreating && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-1560">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-1561">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setIsCreating(false)} data-id="element-1562" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden" data-id="element-1563">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-1564">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-1565">
                  Create New Role
                </h3>
                <button onClick={() => setIsCreating(false)} className="text-gray-400 hover:text-gray-500" data-id="element-1566">
                  <X className="h-5 w-5" data-id="element-1567" />
                </button>
              </div>
              <div className="px-6 py-4 space-y-4 overflow-y-auto max-h-[60vh]" data-id="element-1568">
                <div data-id="element-1569">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1570">
                    Role Name
                  </label>
                  <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={newRole.name} onChange={e => setNewRole({
                ...newRole,
                name: e.target.value
              })} placeholder="e.g., Content Manager" data-id="element-1571" />
                </div>
                <div data-id="element-1572">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1573">
                    Description
                  </label>
                  <textarea rows={2} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={newRole.description} onChange={e => setNewRole({
                ...newRole,
                description: e.target.value
              })} placeholder="Brief description of this role's responsibilities..." data-id="element-1574" />
                </div>
                <div data-id="element-1575">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1576">
                    Permissions
                  </label>
                  {renderPermissionEditor(newRole.permissions, false)}
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3" data-id="element-1577">
                <button onClick={() => setIsCreating(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1578">
                  Cancel
                </button>
                <button onClick={handleCreateRole} disabled={!newRole.name.trim()} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50" data-id="element-1579">
                  Create Role
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Edit Role Modal */}
      {editingRole && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-1580">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-1581">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setEditingRole(null)} data-id="element-1582" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden" data-id="element-1583">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-1584">
                <div data-id="element-1585">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-1586">
                    Edit Role
                  </h3>
                  {roles.find(r => r.id === editingRole)?.isSystem && <p className="text-xs text-purple-600 flex items-center gap-1 mt-1" data-id="element-1587">
                      <Lock className="h-3 w-3" data-id="element-1588" /> System role - name cannot be
                      changed
                    </p>}
                </div>
                <button onClick={() => setEditingRole(null)} className="text-gray-400 hover:text-gray-500" data-id="element-1589">
                  <X className="h-5 w-5" data-id="element-1590" />
                </button>
              </div>
              <div className="px-6 py-4 space-y-4 overflow-y-auto max-h-[60vh]" data-id="element-1591">
                <div data-id="element-1592">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1593">
                    Role Name
                  </label>
                  <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm disabled:bg-gray-100 disabled:text-gray-500" value={editForm.name} onChange={e => setEditForm({
                ...editForm,
                name: e.target.value
              })} disabled={roles.find(r => r.id === editingRole)?.isSystem} data-id="element-1594" />
                </div>
                <div data-id="element-1595">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1596">
                    Description
                  </label>
                  <textarea rows={2} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={editForm.description} onChange={e => setEditForm({
                ...editForm,
                description: e.target.value
              })} data-id="element-1597" />
                </div>
                <div data-id="element-1598">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1599">
                    Permissions
                  </label>
                  {renderPermissionEditor(editForm.permissions, true)}
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3" data-id="element-1600">
                <button onClick={() => setEditingRole(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1601">
                  Cancel
                </button>
                <button onClick={handleSaveEdit} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-1602">
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Roles List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1603">
        {roles.map(role => {
        const roleUsers = getUsersWithRole(role.id);
        const isSelected = selectedRole === role.id;
        return <div key={role.id} className={`bg-white border rounded-lg overflow-hidden transition-all ${isSelected ? 'border-blue-500 ring-2 ring-blue-100' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1604">
              <div className="p-4 cursor-pointer" onClick={() => setSelectedRole(isSelected ? null : role.id)} data-id="element-1605">
                <div className="flex items-start justify-between mb-3" data-id="element-1606">
                  <div className="flex items-center gap-2" data-id="element-1607">
                    <div className={`p-2 rounded-lg ${role.isSystem ? 'bg-purple-50' : 'bg-blue-50'}`} data-id="element-1608">
                      <Shield className={`h-5 w-5 ${role.isSystem ? 'text-purple-600' : 'text-blue-600'}`} data-id="element-1609" />
                    </div>
                    <div data-id="element-1610">
                      <h3 className="font-medium text-gray-900" data-id="element-1611">{role.name}</h3>
                      {role.isSystem && <span className="inline-flex items-center gap-1 text-xs text-purple-600" data-id="element-1612">
                          <Lock className="h-3 w-3" data-id="element-1613" /> System
                        </span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1" data-id="element-1614">
                    <button onClick={e => {
                  e.stopPropagation();
                  handleDuplicateRole(role.id);
                }} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded" title="Duplicate" data-id="element-1615">
                      <Copy className="h-4 w-4" data-id="element-1616" />
                    </button>
                    {/* Allow editing ALL roles (including system roles) */}
                    <button onClick={e => {
                  e.stopPropagation();
                  handleStartEdit(role.id);
                }} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded" title="Edit" data-id="element-1617">
                      <Edit2 className="h-4 w-4" data-id="element-1618" />
                    </button>
                    {/* Only allow deleting non-system roles */}
                    {!role.isSystem && <button onClick={e => {
                  e.stopPropagation();
                  deleteRole(role.id);
                }} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" title="Delete" data-id="element-1619">
                        <Trash2 className="h-4 w-4" data-id="element-1620" />
                      </button>}
                  </div>
                </div>

                <p className="text-sm text-gray-500 mb-3 line-clamp-2" data-id="element-1621">
                  {role.description}
                </p>

                <div className="flex items-center justify-between text-sm" data-id="element-1622">
                  <span className="text-gray-500" data-id="element-1623">
                    <span className="font-medium text-gray-900" data-id="element-1624">
                      {role.permissions.length}
                    </span>{' '}
                    permissions
                  </span>
                  <span className="text-gray-500" data-id="element-1625">
                    <span className="font-medium text-gray-900" data-id="element-1626">
                      {roleUsers.length}
                    </span>{' '}
                    users
                  </span>
                </div>
              </div>

              {/* Expanded Details */}
              {isSelected && <div className="border-t border-gray-100 p-4 bg-gray-50 space-y-3" data-id="element-1627">
                  <div data-id="element-1628">
                    <h4 className="text-xs font-medium text-gray-500 uppercase mb-2" data-id="element-1629">
                      Permissions by Category
                    </h4>
                    <div className="space-y-1" data-id="element-1630">
                      {Object.entries(permissionCategories).map(([category, perms]) => {
                  const count = perms.filter(p => role.permissions.includes(p as Permission)).length;
                  if (count === 0) return null;
                  return <div key={category} className="flex items-center justify-between text-sm" data-id="element-1631">
                              <span className="text-gray-600" data-id="element-1632">{category}</span>
                              <span className={`font-medium ${count === perms.length ? 'text-green-600' : 'text-blue-600'}`} data-id="element-1633">
                                {count}/{perms.length}
                              </span>
                            </div>;
                })}
                    </div>
                  </div>

                  {roleUsers.length > 0 && <div data-id="element-1634">
                      <h4 className="text-xs font-medium text-gray-500 uppercase mb-2" data-id="element-1635">
                        Users with this role
                      </h4>
                      <div className="flex flex-wrap gap-1" data-id="element-1636">
                        {roleUsers.slice(0, 5).map(user => <span key={user.id} className="inline-flex items-center px-2 py-1 rounded text-xs bg-white border border-gray-200 text-gray-700" data-id="element-1637">
                            {user.name}
                          </span>)}
                        {roleUsers.length > 5 && <span className="text-xs text-gray-500" data-id="element-1638">
                            +{roleUsers.length - 5} more
                          </span>}
                      </div>
                    </div>}
                </div>}
            </div>;
      })}
      </div>
    </div>;
}